import { defineStore } from 'pinia'

// Define the interface for a promotional slide
interface PromotionalSlide {
  published: string
  desktop: string
  tablet: string
  mobile: string
  video: boolean
  video_poster: string
  contrast: string
  postion: string
  heading_content: string
  sub_heading: string
  link: string
  button_text: string
  start: string
  end: string
}

// Define the interface for footer banner
interface FooterBanner {
  image: string
  title: string
  content: string
  button: string
  link: string
}

// Define the interface for config data
interface ConfigData {
  promotional?: Array<{
    slides: PromotionalSlide[]
    footerbanner?: FooterBanner[]
    footerblocks?: boolean
  }>
}

// Define the interface for the promotional state
interface PromotionalState {
  slides: PromotionalSlide[]
  thumbs: any[]
  footerbanner: FooterBanner[]
  footerblocks: boolean
  // Realtime subscription state
  realtimeSubscription: any
  isConnected: boolean
  lastUpdate: Date | null
  connectionError: string | null
  // Enhanced error recovery state
  reconnectAttempts: number
  maxReconnectAttempts: number
  reconnectDelay: number
  maxReconnectDelay: number
  isReconnecting: boolean
  lastSuccessfulConnection: Date | null
  consecutiveErrors: number
  isFallbackMode: boolean
  connectionHealthScore: number
  reconnectTimer: NodeJS.Timeout | null
}

export const usePromotionalStore = defineStore('promotional', {
  state: (): PromotionalState => ({
    slides: [],
    thumbs: [],
    footerbanner: [],
    footerblocks: false,
    // Realtime subscription state
    realtimeSubscription: null,
    isConnected: false,
    lastUpdate: null,
    connectionError: null,
    // Enhanced error recovery state
    reconnectAttempts: 0,
    maxReconnectAttempts: 3, // Reduced from 5 to 3
    reconnectDelay: 2000, // Increased from 1 second to 2 seconds
    maxReconnectDelay: 60000, // Increased from 30 to 60 seconds
    isReconnecting: false,
    lastSuccessfulConnection: null,
    consecutiveErrors: 0,
    isFallbackMode: false,
    connectionHealthScore: 100,
    reconnectTimer: null
  }),

  getters: {
    // Get all promotional slides
    promotionalSlides: (state) => state.slides || [],
   
    // Get active promotional slides (based on start and end dates)
    activePromotionalSlides: (state) => {
      const now = new Date()
  
      return state.slides.filter(slide => {
        if (!slide.start || !slide.end) return true
        
        const startDate = new Date(slide.start)
        const endDate = new Date(slide.end)
        
        return now >= startDate && now <= endDate
      })
    },
    
    // Get footer banners
    footerBanners: (state) => state.footerbanner || [],

    // Get connection status for UI
    connectionStatus: (state) => {
      if (state.isReconnecting) return 'reconnecting'
      if (state.isFallbackMode) return 'fallback'
      if (state.isConnected) return 'connected'
      if (state.connectionError) return 'error'
      return 'disconnected'
    },

    // Get connection health indicator
    connectionHealth: (state) => {
      if (state.connectionHealthScore >= 80) return 'excellent'
      if (state.connectionHealthScore >= 60) return 'good'
      if (state.connectionHealthScore >= 40) return 'fair'
      if (state.connectionHealthScore >= 20) return 'poor'
      return 'critical'
    },

    // Get human-readable status message
    statusMessage: (state) => {
      if (state.isReconnecting) return `Reconnecting... (attempt ${state.reconnectAttempts}/${state.maxReconnectAttempts})`
      if (state.isFallbackMode) return 'Using fallback data (realtime unavailable)'
      if (state.isConnected) return 'Real-time updates active'
      if (state.connectionError) return state.connectionError
      return 'Initializing connection...'
    },

    // Check if should attempt reconnection
    canReconnect: (state) => {
      return !state.isReconnecting && 
             state.reconnectAttempts < state.maxReconnectAttempts &&
             !state.isFallbackMode
    }
  },

  actions: {
    // Set promotional slides
    setPromotionalSlides(slides: PromotionalSlide[]) {
      this.slides = slides
      this.lastUpdate = new Date()
    },
    
    // Set footer banners
    setFooterBanners(banners: FooterBanner[]) {
      this.footerbanner = banners
    },

    // Subscribe to realtime changes for promotional slides
    subscribeToPromotionalSlides(dealershipId: string) {
      // Only run on client
      if (process.server) return

      // Cleanup existing subscription
      this.unsubscribe()
      
      try {
        const supabase = useSupabaseClient()
        
        if (!supabase) {
          throw new Error('Supabase client is not available')
        }
        this.realtimeSubscription = supabase
          .channel(`promotional-slides-${dealershipId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'promotional_slides_realtime_triggers',
            filter: `dealership_id=eq.${dealershipId}`
          }, (payload) => {
            this.handleRealtimeTrigger(payload)
          })
          .on('system', { event: 'disconnect' }, () => {
            this.isConnected = false
            this.connectionError = 'Connection lost'
            this.updateConnectionHealth('error')
            this.autoReconnect(dealershipId)
          })
          .on('system', { event: 'reconnect' }, () => {
            this.isConnected = true
            this.connectionError = null
            this.updateConnectionHealth('success')
            this.resetConnectionState()
          })
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              this.isConnected = true
              this.connectionError = null
              this.updateConnectionHealth('success')
              this.resetConnectionState()
            } else if (status === 'CHANNEL_ERROR') {
              this.isConnected = false
              this.connectionError = 'Channel error'
              this.updateConnectionHealth('error')
              this.handleConnectionError(new Error('Channel error'), dealershipId)
            } else if (status === 'TIMED_OUT') {
              this.isConnected = false
              this.connectionError = 'Connection timeout'
              this.updateConnectionHealth('timeout')
              this.handleConnectionError(new Error('Connection timeout'), dealershipId)
            } else if (status === 'CLOSED') {
              this.isConnected = false
              this.connectionError = 'Connection closed'
              this.updateConnectionHealth('error')
              this.autoReconnect(dealershipId)
            }
          })
      } catch (error) {
        console.error('[PromotionalStore] Failed to subscribe to realtime:', error)
        this.handleConnectionError(error, dealershipId)
      }
    },

    // Unsubscribe from realtime
    unsubscribe() {
      if (this.realtimeSubscription) {
        this.realtimeSubscription.unsubscribe()
        this.realtimeSubscription = null
      }
      
      // Clear connection state
      this.isConnected = false
      this.connectionError = null
      this.isReconnecting = false
      
      // Clear timers
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },

    // Handle realtime trigger updates
    async handleRealtimeTrigger(payload: any) {
      const { eventType, new: newRecord, old: oldRecord } = payload
      
      // Refresh data from config when trigger is received
      try {
        await this.refreshFromConfig()
      } catch (error) {
        console.error('[PromotionalStore] Failed to refresh after realtime trigger:', error)
      }
    },

    // Enhanced reconnect with exponential backoff
    async reconnect(dealershipId: string) {
      if (this.isReconnecting || this.reconnectAttempts >= this.maxReconnectAttempts) {
        return
      }

      this.isReconnecting = true
      this.reconnectAttempts++
      this.consecutiveErrors++
      
      // Clear any existing timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }

      // Cleanup existing subscription
      this.unsubscribe()
      
      try {
        // Calculate exponential backoff delay
        const delay = Math.min(
          this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
          this.maxReconnectDelay
        )
        await new Promise(resolve => setTimeout(resolve, delay))
        
        // Attempt to reconnect
        this.subscribeToPromotionalSlides(dealershipId)
        
        // Update connection health score
        this.updateConnectionHealth('reconnect_attempt')
        
      } catch (error) {
        console.error('[PromotionalStore] Reconnection failed:', error)
        this.handleConnectionError(error, dealershipId)
      } finally {
        this.isReconnecting = false
      }
    },

    // Enhanced automatic reconnection with smart retry logic
    async autoReconnect(dealershipId: string) {
      if (!this.canReconnect) {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          await this.enableFallbackMode()
        }
        return
      }

      // Schedule reconnection
      const delay = Math.min(
        this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
        this.maxReconnectDelay
      )
      
      this.reconnectTimer = setTimeout(async () => {
        await this.reconnect(dealershipId)
      }, delay)
    },

    // Load fallback data from config file
    async loadFallbackData() {
      try {
        const configData = await $fetch<ConfigData>('/api/site-config', {
          query: { _refresh: Date.now() }
        })
        
        if (configData?.promotional) {
          this.initializeFromData(configData.promotional)
          console.log('[PromotionalStore] Fallback data loaded')
          return true
        }
      } catch (error) {
        console.error('[PromotionalStore] Fallback data load failed:', error)
      }
      return false
    },
    
    // Initialize promotional data from API response
    initializeFromData(data: any) {
      if (data && Array.isArray(data)) {
        const promotionalData = data[0] || {}
        
        if (promotionalData.slides) {
          this.slides = promotionalData.slides
        }
        
        if (promotionalData.footerbanner) {
          this.footerbanner = promotionalData.footerbanner
        }
        
        if (typeof promotionalData.footerblocks !== 'undefined') {
          this.footerblocks = promotionalData.footerblocks
        }
      }
      
      this.lastUpdate = new Date()
    },

    // Force refresh promotional data from config (bypasses all caches)
    async forceRefreshFromConfig() {
      try {
        // Clear any existing data first
        this.slides = []
        
        // Fetch completely fresh config data with multiple cache-busting techniques
        const configData = await $fetch<ConfigData>('/api/site-config', {
          // Add multiple cache-busting parameters
          query: { 
            _refresh: Date.now(),
            _force: 'true',
            _cache_bust: Math.random().toString(36)
          },
          // Force fresh request
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        
        if (configData && configData.promotional) {
          this.initializeFromData(configData.promotional)
          return true
        }
        
        console.warn('[PromotionalStore] FORCE - No promotional data found in config')
        return false
        
      } catch (error) {
        console.error('[PromotionalStore] FORCE - Error refreshing promotional data:', error)
        return false
      }
    },

    // Refresh promotional data from config
    async refreshFromConfig() {
      try {
        console.log('[PromotionalStore] Refreshing promotional data from config...')
        
        // Fetch fresh config data
        const configData = await $fetch<ConfigData>('/api/site-config', {
          // Add cache-busting parameter to ensure fresh data
          query: { _refresh: Date.now() }
        })
        
        if (configData && configData.promotional) {
          this.initializeFromData(configData.promotional)
          return true
        }
        
        console.warn('[PromotionalStore] No promotional data found in config')
        return false
        
      } catch (error) {
        console.error('[PromotionalStore] Error refreshing promotional data:', error)
        return false
      }
    },

    // Clear all promotional data
    clearData() {
      this.slides = []
      this.footerbanner = []
      this.footerblocks = false
      this.lastUpdate = null
    },

    // Handle connection errors with enhanced error tracking
    handleConnectionError(error: any, dealershipId: string) {
      console.error('[PromotionalStore] Connection error:', error)
      
      this.consecutiveErrors++
      this.connectionError = error?.message || 'Connection failed'
      this.updateConnectionHealth('error')
      
      // If too many consecutive errors, enable fallback mode
      if (this.consecutiveErrors >= 3) {
        console.warn('[PromotionalStore] Too many consecutive errors, enabling fallback mode')
        this.enableFallbackMode()
      } else if (this.canReconnect) {
        // Schedule auto-reconnect
        this.autoReconnect(dealershipId)
      }
    },

    // Enable fallback mode when realtime fails
    async enableFallbackMode() {
      console.log('[PromotionalStore] Enabling fallback mode...')
      
      this.isFallbackMode = true
      this.isConnected = false
      this.isReconnecting = false
      this.connectionError = 'Real-time updates unavailable'
      
      // Clear reconnect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      
      // Load data from config as fallback
      try {
        await this.loadFallbackData()
        console.log('[PromotionalStore] Fallback mode enabled successfully')
      } catch (error) {
        console.error('[PromotionalStore] Failed to load fallback data:', error)
      }
    },

    // Disable fallback mode and attempt to restore realtime
    async disableFallbackMode(dealershipId: string) {
      console.log('[PromotionalStore] Attempting to disable fallback mode...')
      
      this.isFallbackMode = false
      this.reconnectAttempts = 0
      this.consecutiveErrors = 0
      this.connectionHealthScore = 100
      this.connectionError = null
      
      // Attempt to restore realtime connection
      this.subscribeToPromotionalSlides(dealershipId)
    },

    // Update connection health score based on events
    updateConnectionHealth(event: string) {
      switch (event) {
        case 'success':
          this.connectionHealthScore = Math.min(100, this.connectionHealthScore + 10)
          this.consecutiveErrors = 0
          this.lastSuccessfulConnection = new Date()
          break
        case 'error':
          this.connectionHealthScore = Math.max(0, this.connectionHealthScore - 20)
          break
        case 'reconnect_attempt':
          this.connectionHealthScore = Math.max(0, this.connectionHealthScore - 5)
          break
        case 'timeout':
          this.connectionHealthScore = Math.max(0, this.connectionHealthScore - 15)
          break
      }
    },

    // Reset connection state for fresh start
    resetConnectionState() {
      this.reconnectAttempts = 0
      this.consecutiveErrors = 0
      this.connectionHealthScore = 100
      this.isReconnecting = false
      this.isFallbackMode = false
      this.connectionError = null
      this.lastSuccessfulConnection = null
      
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },

    // Force refresh connection (manual intervention)
    async forceReconnect(dealershipId: string) {
      console.log('[PromotionalStore] Force reconnecting...')
      
      // Reset all connection state
      this.resetConnectionState()
      
      // Cleanup and reconnect
      this.unsubscribe()
      this.subscribeToPromotionalSlides(dealershipId)
    }
  }
}) 